/**
 * AppHeader — Premium top bar with profile dropdown menu.
 *
 * Left: clickable user avatar → opens dropdown with:
 *   • Change profile photo
 *   • Settings (language, notifications, sound)
 *   • Logout (red)
 * Right: property switcher dropdown (only when user has >1 property).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModuleConfig } from '../context/ModuleConfigContext';
import { ChevronDown, Check, LogOut, ClipboardList, PlayCircle, FileText, Shield, LifeBuoy, BarChart3 } from 'lucide-react';
import { profileAPI } from '../services/api';

const Avatar = ({ src, name, size = 'md' }) => {
  const dimensions = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm';
  const initial = (name || 'U').trim().charAt(0).toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name || 'profile'}
        className={`${dimensions} rounded-full object-cover ring-2 ring-gold/60 shadow-gold`}
      />
    );
  }
  return (
    <div className={`${dimensions} rounded-full bg-[#1B2A7B] flex items-center justify-center text-white font-semibold ring-2 ring-white/40 shadow-premium`}>
      {initial}
    </div>
  );
};

// iter167 — Profile menu items (order locked by product spec).
// `moduleKey` maps to the admin-panel's `<key>_enabled` toggle, served via
// GET /api/config/modules → ModuleConfigContext (single source of truth).
// iter188 — Analytics moved out of the bottom nav and into this profile
// menu directly below "Onboarding Form".
const PROFILE_MENU_ITEMS = [
  { moduleKey: 'onboarding_form_enabled',  label: 'Onboarding Form',     icon: ClipboardList, path: '/onboarding',            testid: 'menu-onboarding-form' },
  { moduleKey: 'analytics_enabled',        label: 'Analytics',           icon: BarChart3,     path: '/analytics',             testid: 'menu-analytics' },
  { moduleKey: 'tutorial_videos_enabled',  label: 'Tutorial Videos',     icon: PlayCircle,    path: '/tutorial-videos',       testid: 'menu-tutorial-videos' },
  { moduleKey: 'terms_conditions_enabled', label: 'Terms & Conditions',  icon: FileText,      path: '/terms-and-conditions',  testid: 'menu-terms' },
  { moduleKey: 'privacy_policy_enabled',   label: 'Privacy Policy',      icon: Shield,        path: '/privacy-policy',        testid: 'menu-privacy' },
  { moduleKey: 'help_enabled',             label: 'Help',                icon: LifeBuoy,      path: '/help',                  testid: 'menu-help' },
];

const ProfileMenu = ({ user, photo, modules, onClose }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    onClose();
    setTimeout(() => logout(), 50);
  };

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const initial = (user?.name || 'U').trim().charAt(0).toUpperCase();
  const enabledItems = PROFILE_MENU_ITEMS.filter((item) =>
    // Default to enabled if the admin config hasn't loaded yet — keeps items
    // visible on cold cache instead of flashing an empty menu.
    modules ? modules[item.moduleKey] !== false : true
  );

  return (
    <div
      className="absolute left-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-premium-lg border border-gray-100 overflow-hidden animate-fadeIn z-50"
      data-testid="header-profile-menu"
    >
      {/* Owner identity — always at the top, never toggled off */}
      <div className="px-5 py-4 border-b border-gray-100" data-testid="menu-owner-block">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full ring-2 ring-[#1B2A7B]/20 overflow-hidden bg-[#1B2A7B] flex items-center justify-center">
            {photo ? (
              <img src={photo} alt="profile" className="w-full h-full object-cover" />
            ) : (
              <span className="text-base font-semibold text-white">{initial}</span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate" data-testid="menu-owner-name">
              {user?.name || 'Owner'}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5">
              {user?.role === 'staff' ? 'Staff' : 'Owner'}
            </p>
          </div>
        </div>
      </div>

      {/* Configurable module rows */}
      <div className="py-1" data-testid="menu-modules">
        {enabledItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.moduleKey}
              onClick={() => go(item.path)}
              className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#EEF1FC] transition-colors text-left"
              data-testid={item.testid}
            >
              <span className="w-8 h-8 rounded-full bg-[#EEF1FC] flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#1B2A7B]" />
              </span>
              <span className="text-sm font-medium text-gray-800">{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Logout — always last, never toggled off */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-red-50/80 transition-colors text-left border-t border-gray-100"
        data-testid="menu-logout"
      >
        <span className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
          <LogOut className="w-4 h-4 text-red-500" />
        </span>
        <span className="text-sm font-semibold text-red-600">Logout</span>
      </button>
    </div>
  );
};

const AppHeader = ({ subtitle = null }) => {
  const { user, properties, selectedProperty, setSelectedProperty } = useAuth();
  // iter167 — Single source of truth for module toggles. ModuleConfigContext
  // proxies /api/config/modules (which itself proxies the Admin Panel's
  // /api/public/module-config) and already refetches on route change +
  // visibilitychange. Fail-open defaults keep every profile item visible if
  // the Admin Panel is unreachable.
  const { modules } = useModuleConfig();
  const [menuOpen, setMenuOpen] = useState(false);
  const [propertyOpen, setPropertyOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const menuRef = useRef(null);
  const propertyRef = useRef(null);

  // Fetch current photo on mount
  useEffect(() => {
    let mounted = true;
    profileAPI.getPhoto().then((res) => {
      if (mounted && res?.RESULT?.profile_photo) setPhoto(res.RESULT.profile_photo);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [user?.phone]);

  // Listen for global photo-updated events (uploads from elsewhere)
  useEffect(() => {
    const onUpdate = (e) => setPhoto(e.detail?.profile_photo || null);
    window.addEventListener('profile-photo-updated', onUpdate);
    return () => window.removeEventListener('profile-photo-updated', onUpdate);
  }, []);

  // Outside click handlers
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (propertyRef.current && !propertyRef.current.contains(e.target)) setPropertyOpen(false);
    };
    if (menuOpen || propertyOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, propertyOpen]);

  // Close on Escape
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') { setMenuOpen(false); setPropertyOpen(false); } };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  const propertyName = selectedProperty?.property_name || selectedProperty?.name || 'Property';
  const hasMultiple = (properties?.length || 0) > 1;

  return (
    <div
      className="sticky top-0 z-30 bg-[#1B2A7B] shadow-premium"
      data-testid="app-header"
    >
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Left — Profile menu trigger */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center group"
            data-testid="header-profile-btn"
            aria-label="Open profile menu"
            aria-expanded={menuOpen}
          >
            <Avatar src={photo} name={user?.name} />
          </button>

          {menuOpen && (
            <ProfileMenu
              user={user}
              photo={photo}
              modules={modules}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </div>

        <div className="flex-1" />

        {/* Right — Property switcher */}
        <div className="relative" ref={propertyRef}>
          <button
            onClick={() => hasMultiple && setPropertyOpen((v) => !v)}
            disabled={!hasMultiple}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all border ${
              hasMultiple
                ? 'bg-white/10 hover:bg-white/15 border-gold/40 hover:border-gold cursor-pointer'
                : 'bg-white/5 border-white/10 cursor-default'
            }`}
            data-testid="property-switcher-btn"
          >
            <span className="text-xs font-semibold text-white truncate max-w-[180px]">
              {propertyName}
            </span>
            {hasMultiple && (
              <ChevronDown className={`w-4 h-4 text-gold transition-transform duration-200 ${propertyOpen ? 'rotate-180' : ''}`} />
            )}
          </button>

          {propertyOpen && hasMultiple && (
            <div
              className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-premium-lg border border-gray-100 overflow-hidden animate-fadeIn z-50"
              data-testid="property-switcher-dropdown"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-navy/5 to-transparent border-b border-gray-100">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Switch property</p>
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {properties.map((p) => {
                  const isActive = p.id === selectedProperty?.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedProperty(p); setPropertyOpen(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-[#EEF1FC]/70 transition-colors flex items-center justify-between gap-3 ${
                        isActive ? 'bg-[#DDE3FB]/70' : ''
                      }`}
                      data-testid={`property-option-${p.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{p.property_name || p.name}</p>
                        {p.user_role && (
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5">{p.user_role}</p>
                        )}
                      </div>
                      {isActive && <Check className="w-4 h-4 text-[#1B2A7B] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {subtitle && (
        <div className="px-4 pb-3">
          {subtitle}
        </div>
      )}
    </div>
  );
};

export { Avatar };
export default AppHeader;
